import { useEffect, useRef } from 'react';

export default function RobotCharacter() {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let time = 0;
    let smoothX = 0.5, smoothY = 0.5;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const handleMouseMove = (e) => {
      mouse.current.x = e.clientX / window.innerWidth;
      mouse.current.y = e.clientY / window.innerHeight;
    };
    window.addEventListener('mousemove', handleMouseMove);

    const lerp = (a, b, t) => a + (b - a) * t;

    const getAccent = () => {
      const s = getComputedStyle(document.documentElement);
      const hsl = s.getPropertyValue('--primary').trim();
      if (!hsl) return { r: 34, g: 197, b: 94 };
      const parts = hsl.split(/\s+/);
      const h = parseFloat(parts[0]) || 152;
      const sat = parseFloat(parts[1]) / 100 || 0.7;
      const lig = parseFloat(parts[2]) / 100 || 0.48;
      const f = n => { const k = (n + h / 30) % 12; return lig - sat * Math.min(lig, 1 - lig) * Math.max(-1, Math.min(k - 3, 9 - k, 1)); };
      return { r: Math.round(f(0) * 255), g: Math.round(f(8) * 255), b: Math.round(f(4) * 255) };
    };

    const rrect = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    };

    // Simple 2-segment IK solver
    const solveIK = (shoulderX, shoulderY, targetX, targetY, upperLen, lowerLen, flipElbow) => {
      const dxT = targetX - shoulderX;
      const dyT = targetY - shoulderY;
      const dist = Math.sqrt(dxT * dxT + dyT * dyT);
      const maxReach = upperLen + lowerLen - 2;
      const clampedDist = Math.min(dist, maxReach);
      const angle = Math.atan2(dyT, dxT);

      // Law of cosines for elbow angle
      let cosAngle = (upperLen * upperLen + clampedDist * clampedDist - lowerLen * lowerLen) / (2 * upperLen * clampedDist);
      cosAngle = Math.max(-1, Math.min(1, cosAngle));
      const elbowAngle = Math.acos(cosAngle) * (flipElbow ? -1 : 1);

      const elbowX = shoulderX + Math.cos(angle + elbowAngle) * upperLen;
      const elbowY = shoulderY + Math.sin(angle + elbowAngle) * upperLen;

      // Hand position
      const handAngle = Math.atan2(targetY - elbowY, targetX - elbowX);
      const handX = elbowX + Math.cos(handAngle) * lowerLen;
      const handY = elbowY + Math.sin(handAngle) * lowerLen;

      return { elbowX, elbowY, handX, handY };
    };

    const animate = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      time += 0.016;

      smoothX = lerp(smoothX, mouse.current.x, 0.04);
      smoothY = lerp(smoothY, mouse.current.y, 0.04);

      const ac = getAccent();
      const aR = ac.r, aG = ac.g, aB = ac.b;

      // Robot positioned far right
      const cx = w * 0.78;
      const baseY = h * 0.88;
      const bob = Math.sin(time * 1.0) * 5;
      const breathe = Math.sin(time * 0.7) * 2;

      const scale = Math.min(w, h) / 420;
      const s = (v) => v * scale;

      // Cursor direction (normalized -1 to 1)
      const dx = (smoothX - 0.5) * 2;
      const dy = (smoothY - 0.5) * 2;

      // Cursor position in canvas space (relative to robot center)
      const cursorCanvasX = smoothX * w;
      const cursorCanvasY = smoothY * h;

      // VERY pronounced head movement
      const headTiltDeg = dx * 30;
      const headNodPx = dy * -18;
      const headShiftX = dx * s(25);

      // ===== TRACKS =====
      const tkW = s(150), tkH = s(26);
      const tkX = cx - tkW / 2, tkY = baseY - tkH / 2;

      rrect(tkX, tkY, tkW, tkH, s(10));
      const tkGrad = ctx.createLinearGradient(tkX, tkY, tkX, tkY + tkH);
      tkGrad.addColorStop(0, '#32363c');
      tkGrad.addColorStop(1, '#1e2024');
      ctx.fillStyle = tkGrad;
      ctx.fill();
      ctx.strokeStyle = '#44484e';
      ctx.lineWidth = 1;
      ctx.stroke();

      for (let i = 0; i < 9; i++) {
        const tx = tkX + s(10) + i * (tkW - s(20)) / 8;
        ctx.beginPath();
        ctx.moveTo(tx, tkY + s(5));
        ctx.lineTo(tx, tkY + tkH - s(5));
        ctx.strokeStyle = '#16181c';
        ctx.lineWidth = s(3);
        ctx.stroke();
      }
      for (const wx of [tkX + s(18), cx, tkX + tkW - s(18)]) {
        ctx.beginPath();
        ctx.arc(wx, tkY + tkH / 2, s(9), 0, Math.PI * 2);
        ctx.fillStyle = '#16181c';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(wx, tkY + tkH / 2, s(3), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${aR},${aG},${aB},0.5)`;
        ctx.fill();
      }

      // ===== BODY =====
      const bW = s(120), bH = s(140);
      const bX = cx - bW / 2, bY = tkY - bH + s(8) + bob;

      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 25;
      ctx.shadowOffsetY = 10;
      rrect(bX, bY, bW, bH, s(16));
      ctx.fillStyle = '#1a1d22';
      ctx.fill();
      ctx.restore();

      const bGrad = ctx.createLinearGradient(bX, bY, bX + bW, bY + bH);
      bGrad.addColorStop(0, '#2e3138');
      bGrad.addColorStop(0.5, '#22252a');
      bGrad.addColorStop(1, '#1a1d22');
      rrect(bX, bY, bW, bH, s(16));
      ctx.fillStyle = bGrad;
      ctx.fill();
      ctx.strokeStyle = '#3e424a';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Chest screen
      const csW = s(70), csH = s(55);
      const csX = cx - csW / 2 + breathe, csY = bY + s(25);
      rrect(csX, csY, csW, csH, s(8));
      ctx.fillStyle = '#0e1014';
      ctx.fill();
      ctx.strokeStyle = `rgba(${aR},${aG},${aB},0.25)`;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Chest bars (equalizer)
      for (let i = 0; i < 5; i++) {
        const barH = s(8 + Math.sin(time * 3 + i * 1.3) * 8);
        const barX = csX + s(8) + i * s(12);
        const barY2 = csY + csH / 2 + s(5) - barH;
        const barAlpha = 0.5 + Math.sin(time * 2 + i) * 0.3;
        rrect(barX, barY2, s(6), barH, s(2));
        ctx.fillStyle = `rgba(${aR},${aG},${aB},${barAlpha})`;
        ctx.fill();
      }

      // Accent stripe on body
      rrect(bX + s(10), bY + bH - s(18), bW - s(20), s(3), s(1.5));
      ctx.fillStyle = `rgba(${aR},${aG},${aB},0.4)`;
      ctx.fill();

      // ===== ARMS (Both reaching toward cursor) =====
      const armW = s(16);
      const upperLen = s(45);
      const lowerLen = s(40);

      // Shoulder positions
      const lShX = bX - s(4);
      const lShY = bY + s(22) + bob;
      const rShX = bX + bW + s(4);
      const rShY = bY + s(22) + bob;

      // Arm target = cursor position, but with subtle idle sway
      const idleSwayX = Math.sin(time * 0.8) * s(10);
      const idleSwayY = Math.cos(time * 0.6) * s(8);
      const targetX = cursorCanvasX + idleSwayX * 0.15;
      const targetY = cursorCanvasY + idleSwayY * 0.15;

      // Solve IK for left arm (elbow bends outward/up)
      const leftIK = solveIK(lShX, lShY, targetX, targetY, upperLen, lowerLen, true);
      // Solve IK for right arm (elbow bends outward/up)
      const rightIK = solveIK(rShX, rShY, targetX, targetY, upperLen, lowerLen, false);

      // Draw arms function
      const drawArm = (shX, shY, ik) => {
        // Upper arm
        ctx.beginPath();
        ctx.moveTo(shX, shY);
        ctx.lineTo(ik.elbowX, ik.elbowY);
        ctx.strokeStyle = '#2e3138';
        ctx.lineWidth = armW;
        ctx.lineCap = 'round';
        ctx.stroke();

        // Lower arm
        ctx.beginPath();
        ctx.moveTo(ik.elbowX, ik.elbowY);
        ctx.lineTo(ik.handX, ik.handY);
        ctx.strokeStyle = '#2e3138';
        ctx.lineWidth = armW;
        ctx.lineCap = 'round';
        ctx.stroke();

        // Elbow joint (accent glow)
        ctx.beginPath();
        ctx.arc(ik.elbowX, ik.elbowY, s(7), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${aR},${aG},${aB},0.5)`;
        ctx.fill();

        // Hand (gripper)
        ctx.beginPath();
        ctx.arc(ik.handX, ik.handY, s(10), 0, Math.PI * 2);
        ctx.fillStyle = '#3a3d42';
        ctx.fill();
        ctx.strokeStyle = `rgba(${aR},${aG},${aB},0.3)`;
        ctx.lineWidth = 2;
        ctx.stroke();

        // Finger details on hand
        const handAngle = Math.atan2(ik.handY - ik.elbowY, ik.handX - ik.elbowX);
        for (let f = -1; f <= 1; f += 1) {
          const fingerAngle = handAngle + f * 0.4;
          const fLen = s(7);
          ctx.beginPath();
          ctx.moveTo(ik.handX + Math.cos(fingerAngle) * s(8), ik.handY + Math.sin(fingerAngle) * s(8));
          ctx.lineTo(ik.handX + Math.cos(fingerAngle) * (s(8) + fLen), ik.handY + Math.sin(fingerAngle) * (s(8) + fLen));
          ctx.strokeStyle = '#4a4d52';
          ctx.lineWidth = s(4);
          ctx.lineCap = 'round';
          ctx.stroke();
        }

        // Shoulder joint
        ctx.beginPath();
        ctx.arc(shX, shY, s(8), 0, Math.PI * 2);
        ctx.fillStyle = '#44484e';
        ctx.fill();
        ctx.strokeStyle = `rgba(${aR},${aG},${aB},0.25)`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      };

      drawArm(lShX, lShY, leftIK);
      drawArm(rShX, rShY, rightIK);

      // ===== NECK =====
      const nkW = s(36), nkH = s(18);
      const nkX = cx - nkW / 2, nkY = bY - nkH + s(6) + bob;
      rrect(nkX, nkY, nkW, nkH, s(5));
      ctx.fillStyle = '#2a2d32'; ctx.fill();

      // ===== HEAD ===== (BIG, pronounced movement)
      const hdW = s(115), hdH = s(90);
      const headCX = cx + headShiftX;
      const hdX = headCX - hdW / 2;
      const hdY = nkY - hdH + s(8) + bob + headNodPx;

      ctx.save();
      ctx.translate(headCX, hdY + hdH / 2);
      ctx.rotate((headTiltDeg * Math.PI) / 180);
      ctx.translate(-headCX, -(hdY + hdH / 2));

      // Head shadow
      ctx.save();
      ctx.shadowColor = `rgba(${aR},${aG},${aB},0.15)`;
      ctx.shadowBlur = 30;
      ctx.shadowOffsetY = 8;
      rrect(hdX, hdY, hdW, hdH, s(18));
      ctx.fillStyle = '#22252a';
      ctx.fill();
      ctx.restore();

      // Head body
      const hGrad = ctx.createLinearGradient(hdX, hdY, hdX, hdY + hdH);
      hGrad.addColorStop(0, '#383c44');
      hGrad.addColorStop(1, '#22252a');
      rrect(hdX, hdY, hdW, hdH, s(18));
      ctx.fillStyle = hGrad;
      ctx.fill();
      ctx.strokeStyle = `rgba(${aR},${aG},${aB},0.2)`;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Face visor
      const vsW = hdW - s(18), vsH = hdH - s(22);
      const vsX = hdX + s(9), vsY = hdY + s(11);
      rrect(vsX, vsY, vsW, vsH, s(12));
      const vGrad = ctx.createRadialGradient(headCX, vsY + vsH / 2, 0, headCX, vsY + vsH / 2, vsW * 0.7);
      vGrad.addColorStop(0, `rgba(${aR},${aG},${aB},0.1)`);
      vGrad.addColorStop(1, `rgba(${aR},${aG},${aB},0.02)`);
      ctx.fillStyle = '#0c0e12';
      ctx.fill();
      rrect(vsX, vsY, vsW, vsH, s(12));
      ctx.fillStyle = vGrad;
      ctx.fill();
      ctx.strokeStyle = `rgba(${aR},${aG},${aB},0.4)`;
      ctx.lineWidth = 2;
      ctx.stroke();

      // ===== EYES =====
      const eyeSpacing = s(36);
      const eyeCY = vsY + vsH * 0.45;
      const eyeLX = headCX - eyeSpacing / 2;
      const eyeRX = headCX + eyeSpacing / 2;
      const eyeRad = s(18);
      const pupilMax = s(7);
      const pX = dx * pupilMax;
      const pY = dy * pupilMax;

      for (const ex of [eyeLX, eyeRX]) {
        ctx.beginPath();
        ctx.arc(ex, eyeCY, eyeRad + s(6), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${aR},${aG},${aB},0.08)`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(ex, eyeCY, eyeRad + s(2), 0, Math.PI * 2);
        ctx.fillStyle = '#0a0c10';
        ctx.fill();

        const eGrad = ctx.createRadialGradient(ex + pX * 0.5, eyeCY + pY * 0.5, 0, ex, eyeCY, eyeRad);
        eGrad.addColorStop(0, `rgba(${aR},${aG},${aB},1)`);
        eGrad.addColorStop(0.5, `rgba(${aR},${aG},${aB},0.8)`);
        eGrad.addColorStop(1, `rgba(${aR},${aG},${aB},0.15)`);
        ctx.beginPath();
        ctx.arc(ex, eyeCY, eyeRad, 0, Math.PI * 2);
        ctx.fillStyle = eGrad;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(ex + pX, eyeCY + pY, s(7), 0, Math.PI * 2);
        ctx.fillStyle = '#050608';
        ctx.fill();

        ctx.beginPath();
        ctx.arc(ex + pX, eyeCY + pY, s(3), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${aR},${aG},${aB},0.5)`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(ex + pX - s(3), eyeCY + pY - s(4), s(3.5), 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(ex + pX + s(2), eyeCY + pY + s(2), s(1.5), 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.fill();
      }

      // Mouth/speaker
      const mouthW = s(28), mouthH = s(6);
      const mouthX = headCX - mouthW / 2;
      const mouthY = vsY + vsH - s(14);
      rrect(mouthX, mouthY, mouthW, mouthH, s(3));
      ctx.fillStyle = `rgba(${aR},${aG},${aB},0.2)`;
      ctx.fill();
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.arc(mouthX + s(4) + i * s(5.5), mouthY + mouthH / 2, s(1.5), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${aR},${aG},${aB},${0.3 + Math.sin(time * 4 + i) * 0.2})`;
        ctx.fill();
      }

      // ===== ANTENNA =====
      const antBaseY = hdY;
      const antTipY = antBaseY - s(30) + Math.sin(time * 2.5) * s(4);
      const antTipX = headCX + dx * s(12);

      ctx.beginPath();
      ctx.moveTo(headCX, antBaseY);
      ctx.quadraticCurveTo(headCX + dx * s(6), antBaseY - s(15), antTipX, antTipY);
      ctx.strokeStyle = '#44484e';
      ctx.lineWidth = s(3);
      ctx.lineCap = 'round';
      ctx.stroke();

      const orbPulse = Math.sin(time * 3) * 0.3 + 0.7;
      ctx.beginPath();
      ctx.arc(antTipX, antTipY, s(7), 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${aR},${aG},${aB},${orbPulse})`;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(antTipX, antTipY, s(14), 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${aR},${aG},${aB},${orbPulse * 0.12})`;
      ctx.fill();

      // Ear caps
      for (const side of [-1, 1]) {
        const earX = headCX + side * (hdW / 2 + s(4));
        const earY = hdY + hdH / 2;
        rrect(earX - s(5), earY - s(10), s(10), s(20), s(4));
        ctx.fillStyle = '#2a2d32';
        ctx.fill();
        ctx.strokeStyle = `rgba(${aR},${aG},${aB},0.2)`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      ctx.restore(); // Head rotation

      // ===== GROUND GLOW =====
      const gGlow = ctx.createRadialGradient(cx, baseY + s(8), 0, cx, baseY + s(8), s(110));
      gGlow.addColorStop(0, `rgba(${aR},${aG},${aB},0.1)`);
      gGlow.addColorStop(1, 'transparent');
      ctx.fillStyle = gGlow;
      ctx.fillRect(cx - s(130), baseY - s(10), s(260), s(40));

      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ zIndex: 1 }}
      data-testid="robot-character"
    />
  );
}
